import React from 'react';
import { useTranslation } from 'react-i18next';

export interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Current page number (1-based) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Maximum number of page buttons to show */
  maxVisiblePages?: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Enable/disable next and previous buttons */
  showNavigationButtons?: boolean;
  /** Enable/disable first and last page buttons */
  showFirstLastButtons?: boolean;
  /** Text for previous button */
  previousLabel?: string;
  /** Text for next button */
  nextLabel?: string;
  /** Size of the pagination component */
  size?: 'sm' | 'md' | 'lg';
  /** Disable all buttons */
  disabled?: boolean;
}

export const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      currentPage,
      totalPages,
      maxVisiblePages = 5,
      onPageChange,
      showNavigationButtons = true,
      showFirstLastButtons = false,
      previousLabel,
      nextLabel,
      size = 'md',
      disabled = false,
      className = '',
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    // Ensure current page is within bounds
    const page = Math.max(1, Math.min(currentPage, totalPages));
    
    // Default labels
    const defaultPreviousLabel = t('common.actions.back');
    const defaultNextLabel = t('common.actions.next');
    
    // Callback for page change
    const handlePageChange = (newPage: number) => {
      if (disabled) return;
      
      // Ensure the new page is within bounds
      const validPage = Math.max(1, Math.min(newPage, totalPages));
      
      if (validPage !== page) {
        onPageChange(validPage);
      }
    };
    
    // Generate array of page numbers to display
    const getVisiblePages = (): number[] => {
      // If total pages is less than or equal to max visible pages, show all pages
      if (totalPages <= maxVisiblePages) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }
      
      // Calculate start and end page numbers
      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
      let endPage = startPage + maxVisiblePages - 1;
      
      // Adjust if end page exceeds total pages
      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      return Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
      );
    };
    
    const visiblePages = getVisiblePages();
    
    // Determine size classes
    const sizeClasses = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };
    
    // Button base classes
    const buttonBaseClasses = `
      flex items-center justify-center
      px-3 py-2
      border border-gray-300 dark:border-gray-600
      focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
      transition-colors duration-150
      ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      ${sizeClasses[size]}
    `;
    
    // Active button classes
    const activeButtonClasses = `
      bg-blue-500 dark:bg-blue-600
      text-white
      border-blue-500 dark:border-blue-600
      ${disabled ? '' : 'hover:bg-blue-600 dark:hover:bg-blue-700'}
    `;
    
    // Inactive button classes
    const inactiveButtonClasses = `
      bg-white dark:bg-gray-800
      text-gray-700 dark:text-gray-300
      ${disabled ? '' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
    `;
    
    // Navigation button classes
    const navButtonClasses = `
      ${buttonBaseClasses}
      ${inactiveButtonClasses}
    `;
    
    return (
      <div
        ref={ref}
        className={`flex items-center space-x-1 ${className}`}
        {...props}
      >
        {/* First page button */}
        {showFirstLastButtons && (
          <button
            type="button"
            onClick={() => handlePageChange(1)}
            disabled={disabled || page === 1}
            className={`
              ${navButtonClasses}
              rounded-l-md
              ${page === 1 ? 'opacity-50' : ''}
            `}
            aria-label="First page"
          >
            <span aria-hidden="true">&laquo;</span>
          </button>
        )}
        
        {/* Previous button */}
        {showNavigationButtons && (
          <button
            type="button"
            onClick={() => handlePageChange(page - 1)}
            disabled={disabled || page === 1}
            className={`
              ${navButtonClasses}
              ${!showFirstLastButtons ? 'rounded-l-md' : ''}
              ${page === 1 ? 'opacity-50' : ''}
            `}
            aria-label={previousLabel || defaultPreviousLabel}
          >
            {previousLabel || defaultPreviousLabel}
          </button>
        )}
        
        {/* Page buttons */}
        {visiblePages.map((pageNum) => (
          <button
            key={pageNum}
            type="button"
            onClick={() => handlePageChange(pageNum)}
            disabled={disabled}
            className={`
              ${buttonBaseClasses}
              ${pageNum === page ? activeButtonClasses : inactiveButtonClasses}
            `}
            aria-current={pageNum === page ? 'page' : undefined}
            aria-label={`Page ${pageNum}`}
          >
            {pageNum}
          </button>
        ))}
        
        {/* Next button */}
        {showNavigationButtons && (
          <button
            type="button"
            onClick={() => handlePageChange(page + 1)}
            disabled={disabled || page === totalPages}
            className={`
              ${navButtonClasses}
              ${!showFirstLastButtons ? 'rounded-r-md' : ''}
              ${page === totalPages ? 'opacity-50' : ''}
            `}
            aria-label={nextLabel || defaultNextLabel}
          >
            {nextLabel || defaultNextLabel}
          </button>
        )}
        
        {/* Last page button */}
        {showFirstLastButtons && (
          <button
            type="button"
            onClick={() => handlePageChange(totalPages)}
            disabled={disabled || page === totalPages}
            className={`
              ${navButtonClasses}
              rounded-r-md
              ${page === totalPages ? 'opacity-50' : ''}
            `}
            aria-label="Last page"
          >
            <span aria-hidden="true">&raquo;</span>
          </button>
        )}
      </div>
    );
  }
);

Pagination.displayName = 'Pagination';

export default Pagination;